/**
 * Storyboard Continuity Audit — DPS Required banner.
 *
 * Renders inside the Storyboard panel when the auto pipeline has
 * paused at the Storyboard stage because no approved Detailed
 * Production Script (DPS) exists yet.
 *
 * The button smooth-scrolls to the existing DetailedProductionScriptPanel
 * which is ALREADY mounted on the same Storyboard tab — this banner
 * does NOT auto-click "Generate DPS" and does NOT trigger any AI call.
 */
import { AlertCircle, ArrowDown } from "lucide-react";

export default function DpsRequiredBanner({ project }) {
    const status = (project?.stages?.storyboard?.status || "").toLowerCase();
    if (status !== "awaiting_detailed_production_script") return null;

    const checkpointMessage =
        project?.stages?.storyboard?.checkpoint_state?.message ||
        "Generate and approve the Detailed Production Script before the storyboard agent can run.";

    const scrollToDps = () => {
        // The DPS panel mounts itself on the storyboard tab. Find the
        // first heading/section the panel renders and scroll to it.
        const candidates = [
            "[data-testid=\"detailed-production-script-panel\"]",
            "[data-testid=\"dps-panel\"]",
            "[data-testid=\"detailed-script-panel\"]",
        ];
        let el = null;
        for (const sel of candidates) {
            el = document.querySelector(sel);
            if (el) break;
        }
        if (!el) {
            // Fall back to text match — the existing panel uses the
            // visible title "Detailed Production Script".
            const headings = document.querySelectorAll("h1, h2, h3, h4");
            for (const h of headings) {
                if ((h.textContent || "").toLowerCase().includes("detailed production script")) {
                    el = h;
                    break;
                }
            }
        }
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
            el.classList.add("ring-2", "ring-amber-400");
            setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 1800);
        }
    };

    return (
        <div
            data-testid="dps-required-banner"
            className="mx-6 lg:mx-10 mt-4 mb-2 border border-amber-500/40 bg-amber-500/5 rounded-sm px-5 py-4 flex items-start gap-4"
        >
            <AlertCircle className="w-5 h-5 text-amber-300 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="label-micro text-amber-300 mb-1">Storyboard paused</div>
                <h4 className="font-display text-base tracking-tight text-white mb-1">
                    Detailed Production Script required
                </h4>
                <p className="text-xs text-amber-100/80">
                    {checkpointMessage}
                </p>
            </div>
            <button
                type="button"
                onClick={scrollToDps}
                data-testid="dps-required-banner-cta"
                className="shrink-0 inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider px-3 py-2 rounded-sm border border-amber-400 bg-amber-400 text-black hover:bg-amber-300 transition-colors"
            >
                Open DPS Panel
                <ArrowDown className="w-3.5 h-3.5" />
            </button>
        </div>
    );
}
